import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Calendar,
  Trash2,
  Check,
  Clock,
  Play,
  AlertTriangle,
  ImageIcon,
  FileText,
  Timer,
  UserPlus,
  Repeat,
  Lock,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  getStatusIcon,
  getStatusBadge,
  getPriorityBadge,
  calculateLaborCost,
  calculateTotalCost,
} from './utils';

/**
 * MaintenanceTaskCard — single row card for a maintenance task.
 * Purely presentational. Side-effects (status change, worker
 * assignment, dialogs, delete) come from callbacks so this component
 * stays trivially testable.
 */
export const MaintenanceTaskCard = ({
  task,
  workers,
  propertyName,
  onStatusChange,
  onAssignWorker,
  onOpenPhotos,
  onOpenNotes,
  onDelete,
  onPreviewPhoto,
}) => {
  if (task._restricted) {
    return (
      <Card
        className="bg-white border border-slate-100 opacity-80"
        data-testid={`maintenance-card-restricted-${task.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                {getStatusIcon(task.status)}
                <span className="font-medium text-slate-900 truncate">
                  {propertyName}
                </span>
              </div>
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(parseISO(task.scheduled_date), 'MMM d, yyyy')}
              </p>
              <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5 bg-slate-50 rounded px-2 py-1 w-fit">
                <Lock className="h-3 w-3" />
                Shared without details — ask a manager for access
              </p>
            </div>
            <span
              className={`text-xs px-2 py-1 rounded border ${getStatusBadge(task.status)}`}
              data-testid={`status-badge-${task.id}`}
            >
              {task.status.replace('_', ' ')}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
  <Card
    className={`bg-white border card-hover ${task.priority === 'urgent' ? 'border-red-200' : 'border-slate-100'}`}
    data-testid={`maintenance-card-${task.id}`}
  >
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {task.priority === 'urgent' && (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            )}
            <h3 className="font-semibold text-slate-900 font-manrope">{task.title}</h3>
            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getPriorityBadge(task.priority)}`}>
              {task.priority}
            </span>
            {task.is_recurring && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200 flex items-center gap-1">
                <Repeat className="h-3 w-3" />
                {task.recurrence_pattern}
              </span>
            )}
          </div>

          <p className="text-sm text-slate-500 mb-3">{propertyName}</p>

          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="flex items-center gap-1 text-slate-600">
              <Calendar className="h-4 w-4" />
              {format(parseISO(task.scheduled_date), 'MMM d, yyyy')}
            </span>
            {task.hours_worked > 0 && (
              <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                <Timer className="h-3 w-3" />
                {task.hours_worked}h
              </span>
            )}
            {task.estimated_cost > 0 && (
              <span className="text-slate-600">
                Materials: ${task.estimated_cost.toLocaleString()}
              </span>
            )}
            {calculateLaborCost(task) > 0 && (
              <span className="text-blue-600">Labor: ${calculateLaborCost(task).toFixed(2)}</span>
            )}
            {calculateTotalCost(task) > 0 && (
              <span className="text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded">
                Total: ${calculateTotalCost(task).toFixed(2)}
              </span>
            )}
          </div>

          {task.description && (
            <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded mt-3">
              {task.description}
            </p>
          )}

          {task.assigned_to && (
            <p className="text-xs text-slate-500 mt-2">
              Assigned to: <span className="font-medium">{task.assigned_to}</span>
            </p>
          )}

          {task.share_acks && task.share_acks.length > 0 && (
            <div className="mt-2 space-y-1" data-testid={`share-acks-${task.id}`}>
              {task.share_acks.map((a, i) => (
                <div
                  key={`${a.user_id}-${a.replied_at || i}`}
                  className="inline-flex items-center gap-1.5 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 mr-1"
                >
                  <Check className="h-3 w-3" />
                  <span className="font-medium">{a.user_name}:</span>
                  <span className="text-emerald-700">{a.text}</span>
                </div>
              ))}
            </div>
          )}

          {!task.assigned_to && workers.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 text-xs"
                  data-testid={`assign-maint-worker-btn-${task.id}`}
                >
                  <UserPlus className="h-3 w-3 mr-1" />
                  Assign Worker
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {workers.map((worker) => (
                  <DropdownMenuItem
                    key={worker.id}
                    onClick={() => onAssignWorker(task.id, worker.id)}
                    data-testid={`assign-maint-worker-${worker.id}-${task.id}`}
                  >
                    {worker.name} - ${worker.hourly_rate}/hr
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {task.work_notes && task.work_notes.length > 0 && (
            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {task.work_notes.length} work note{task.work_notes.length > 1 ? 's' : ''}
            </p>
          )}

          {task.photos && task.photos.length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {task.photos.slice(0, 4).map((photo, idx) => (
                <img
                  key={`task-photo-${photo.slice(-20)}-${idx}`}
                  src={photo}
                  alt={`Photo ${idx + 1}`}
                  className="w-16 h-16 object-cover rounded cursor-pointer border border-slate-200 hover:border-slate-400 transition-colors"
                  onClick={() => onPreviewPhoto(photo)}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/64?text=!';
                  }}
                />
              ))}
              {task.photos.length > 4 && (
                <div
                  className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center text-sm text-slate-600 cursor-pointer hover:bg-slate-200"
                  onClick={() => onOpenPhotos(task)}
                >
                  +{task.photos.length - 4}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenNotes(task)}
            className="text-slate-600 hover:text-slate-900"
            data-testid={`maint-notes-btn-${task.id}`}
            title="Work Notes & Hours"
          >
            <FileText className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenPhotos(task)}
            className="text-slate-600 hover:text-slate-900"
            data-testid={`maint-photos-btn-${task.id}`}
          >
            <ImageIcon className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`border ${getStatusBadge(task.status)}`}
                data-testid={`maint-status-btn-${task.id}`}
              >
                {getStatusIcon(task.status)}
                <span className="ml-2">{task.status.replace('_', ' ')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => onStatusChange(task.id, 'pending')}
                data-testid={`maint-status-pending-${task.id}`}
              >
                <Clock className="h-4 w-4 mr-2 text-amber-600" />
                Pending
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onStatusChange(task.id, 'in_progress')}
                data-testid={`maint-status-progress-${task.id}`}
              >
                <Play className="h-4 w-4 mr-2 text-blue-600" />
                In Progress
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onStatusChange(task.id, 'completed')}
                data-testid={`maint-status-completed-${task.id}`}
              >
                <Check className="h-4 w-4 mr-2 text-emerald-600" />
                Completed
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(task)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid={`delete-maintenance-${task.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
  );
};

export default MaintenanceTaskCard;
